// What "direction" means for an Origin-Destination request, and how it turns into the job bodies
// the backend already understands. Pure: no React, router or Firebase imports.
import type { ODMResult } from '../services/odmApi';

/**
 * - 'to-from': every trip with at least one end in the selection; the other end is anywhere.
 * - 'between': trips with both ends in the selection (with one area, trips within that area).
 */
export type OdmDirection = 'to-from' | 'between';

export const DEFAULT_ODM_DIRECTION: OdmDirection = 'to-from';

export const ODM_DIRECTIONS: ReadonlyArray<{ value: OdmDirection; label: string; help: string }> = [
  { value: 'to-from', label: 'To / from these areas', help: 'Trips that start or end in the selection. The other end can be anywhere.' },
  { value: 'between', label: 'Between these areas', help: 'Trips that start and end in the selection. With one area, trips within it.' },
];

export const isOdmDirection = (v: unknown): v is OdmDirection => v === 'to-from' || v === 'between';

/** "Anywhere" for one side of a request: no ids named. Only the demo API understands it. */
export const ANYWHERE: readonly (string | number)[] = Object.freeze([]) as readonly (string | number)[];

/** One request's two sides. `ANYWHERE` (an empty list) means that end can be any area of the same type. */
export interface OdmSides {
  origin: readonly (string | number)[];
  destination: readonly (string | number)[];
}

/**
 * The requests a direction needs for a selection's ids.
 * - between: one request, origin and destination both the selection.
 * - to-from: two requests, the selection to anywhere and anywhere to the selection. The backend
 *   job takes no "anywhere" yet, so these are only sent to the demo API.
 */
export function odmRequestSides(direction: OdmDirection, ids: readonly (string | number)[]): OdmSides[] {
  if (direction === 'between') return [{ origin: ids, destination: ids }];
  return [
    { origin: ids, destination: ANYWHERE },
    { origin: ANYWHERE, destination: ids },
  ];
}

/**
 * One list of rows from the results of several requests. A trip that starts and ends inside the
 * selection is in both halves of a to-from pair; it is kept once.
 */
export function mergeOdmRows(parts: readonly (readonly ODMResult[])[]): ODMResult[] {
  const seen = new Set<string>();
  const out: ODMResult[] = [];
  for (const part of parts) {
    for (const row of part) {
      const key = `${row.origin_geo_id}|${row.destination_geo_id}|${row.time_bucket ?? row.batch_date}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
  }
  return out;
}

export function directionLabel(direction: OdmDirection): string {
  return ODM_DIRECTIONS.find((d) => d.value === direction)?.label ?? direction;
}
